import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** What the user picked. `system` follows the OS and keeps following it. */
export type ThemeChoice = 'light' | 'dark' | 'system';

/** What is actually on screen — `system` has been resolved away. */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system'];

/**
 * Also hard-coded in each app's `index.html` (the website's `src/root.tsx`),
 * in the tiny script that runs before first paint. Change it here and
 * change it there, or a reload will flash the wrong theme before React
 * catches up.
 */
export const THEME_STORAGE_KEY = 'zuruflex.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** The OS preference, or `light` where there is no window to ask. */
export function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

function readStoredChoice(fallback: ThemeChoice): ThemeChoice {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(stored) ? stored : fallback;
  } catch {
    // Safari in private mode, or a browser with storage switched off. The
    // theme still works for the session; it just will not be remembered.
    return fallback;
  }
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemTheme() : choice;
}

interface ThemeContextValue {
  /** The stored preference, including `system`. */
  theme: ThemeChoice;
  /** The theme in force right now. */
  resolvedTheme: ResolvedTheme;
  setTheme: (choice: ThemeChoice) => void;
  /** Flip between light and dark, pinning the result — never back to `system`. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  /** Used until the visitor chooses. Defaults to following the OS. */
  defaultTheme?: ThemeChoice;
  /**
   * Browser-chrome colour per theme, written to `<meta name="theme-color">`.
   * Left alone when omitted — the landing page keeps its brand yellow.
   */
  themeColor?: { light: string; dark: string };
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  themeColor,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeChoice>(() => readStoredChoice(defaultTheme));
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredChoice(defaultTheme)),
  );

  // The very first paint is already correct — index.html saw to that — so
  // easing that one has nothing to dissolve from and only delays the page.
  const isFirstApply = useRef(true);

  useEffect(() => {
    const root = document.documentElement;

    const apply = (next: ResolvedTheme) => {
      setResolvedTheme(next);
      root.style.colorScheme = next;

      const alreadyRight = root.classList.contains('dark') === (next === 'dark');
      const isFirst = isFirstApply.current;
      isFirstApply.current = false;
      if (alreadyRight) return;

      if (!isFirst) {
        root.classList.add('theme-transition');
        window.setTimeout(() => root.classList.remove('theme-transition'), 220);
      }
      root.classList.toggle('dark', next === 'dark');
    };

    apply(resolveTheme(theme));

    if (theme !== 'system' || !window.matchMedia) return;

    // Only `system` tracks the OS. A pinned choice ignores it by design.
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => apply(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  useEffect(() => {
    if (!themeColor) return;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = themeColor[resolvedTheme];
  }, [themeColor, resolvedTheme]);

  const setTheme = useCallback((choice: ThemeChoice) => {
    setThemeState(choice);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch {
      // Storage refused; the choice still applies for this session.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    // Flips what is on screen rather than what was stored, so the first
    // click while on `system` goes to the opposite of what you can see.
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Throws outside a ThemeProvider rather than guessing: a toggle that
 * silently does nothing is far harder to notice than a blank screen.
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a <ThemeProvider>');
  return context;
}
